import { AlertCircle, AlertTriangle, Info } from "lucide-react";
import { useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { INCIDENT_CASE_REFERENCE_MAX, INCIDENT_NOTE_MAX } from "../constants/paymentIncidents.constants";
import type {
  CreatePaymentIncidentEventPayload,
  PaymentIncident,
  PaymentIncidentResolution,
  RecordableIncidentAction,
} from "../types/paymentIncidents.types";
import { incidentOccurredAt } from "../utils/paymentIncidents";
import { IncidentAttachmentsPicker } from "./IncidentAttachmentsPicker";

const FORM_ID = "payment-incident-event";
const RESOLUTIONS: PaymentIncidentResolution[] = ["beneficiary_credited", "status_updated", "other"];

/** `YYYY-MM-DD` of a local day. */
function localDay(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export interface IncidentEventDialogProps {
  action: RecordableIncidentAction | null;
  incident: PaymentIncident;
  provider: string;
  onClose: () => void;
  /** Records the entry. Throws a message to show. */
  onSubmit: (payload: CreatePaymentIncidentEventPayload) => Promise<void>;
}

/**
 * One entry of the timeline: a follow-up note, the provider's answer (with its case number), the provider
 * returning the money, closing or reopening. Screenshots ride along with any of them.
 */
export function IncidentEventDialog({
  action,
  incident,
  provider,
  onClose,
  onSubmit,
}: IncidentEventDialogProps) {
  if (!action) return null;
  return (
    <IncidentEventDialogContent
      key={action}
      action={action}
      incident={incident}
      provider={provider}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  );
}

function IncidentEventDialogContent({
  action,
  incident,
  provider,
  onClose,
  onSubmit,
}: IncidentEventDialogProps & { action: RecordableIncidentAction }) {
  const { t } = useTranslations("paymentIncidents");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = localDay();
  const takesDate = action === "provider_responded" || action === "provider_refunded" || action === "note";

  const schema = useMemo(
    () =>
      z.object({
        note: z.string().max(INCIDENT_NOTE_MAX),
        provider_case_reference: z.string().trim().max(INCIDENT_CASE_REFERENCE_MAX),
        day: z.string(),
        resolution: z.enum(["beneficiary_credited", "status_updated", "other"]),
      }),
    [],
  );
  const form = useZodForm(schema, {
    note: "",
    provider_case_reference: incident.provider_case_reference ?? "",
    day: today,
    resolution: incident.type === "status_not_updated" ? "status_updated" : "beneficiary_credited",
  });
  const resolution = useWatch({ control: form.control, name: "resolution" });
  const doubleRefundRisk =
    action === "resolved" && resolution === "beneficiary_credited" && incident.client_refunded;

  const submit = async (values: z.output<typeof schema>) => {
    const note = values.note.trim();
    if (action === "note" && !note && !files.length) {
      form.setError("note", { message: t("event_validation_note") });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        action,
        note: note || undefined,
        provider_case_reference:
          action === "provider_responded" ? values.provider_case_reference.trim() || undefined : undefined,
        occurred_at: takesDate ? incidentOccurredAt(values.day, today) : undefined,
        resolution: action === "resolved" ? values.resolution : undefined,
        attachments: files,
      });
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : t("event_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && !submitting) onClose();
      }}
      size="lg"
      title={t(`event_dialog_title_${action}`, { provider })}
      description={t(`event_dialog_description_${action}`, { provider })}
      showCloseButton={!submitting}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={onClose} disabled={submitting}>
            {t("action_cancel")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            form={FORM_ID}
            variant={action === "dismissed" ? "destructive" : "primary"}
            loading={submitting}
            disabled={submitting}
          >
            {t(`event_dialog_confirm_${action}`, { provider })}
          </ColocaButton>
        </>
      }
    >
      <ColocaForm id={FORM_ID} form={form} onSubmit={submit} className="space-y-5">
        {action === "provider_refunded" && (
          <ColocaAlert
            variant="secondary"
            icon={Info}
            text={t(
              incident.client_refunded
                ? "event_provider_refunded_info_refunded"
                : "event_provider_refunded_info",
              { provider },
            )}
          />
        )}
        {action === "resolved" && (
          <ColocaSelectField
            name="resolution"
            label={t("event_field_resolution")}
            items={RESOLUTIONS.map((value) => ({ value, label: t(`resolution_${value}`) }))}
            disabled={submitting}
          />
        )}
        {doubleRefundRisk && (
          <ColocaAlert variant="warning" icon={AlertTriangle} text={t("event_resolved_refunded_warning")} />
        )}
        {action === "provider_responded" && (
          <ColocaInputField
            name="provider_case_reference"
            label={t("event_field_case_reference")}
            description={t("event_field_case_reference_hint", { provider })}
            placeholder="CS0101501"
            maxLength={INCIDENT_CASE_REFERENCE_MAX}
            disabled={submitting}
          />
        )}
        {takesDate && (
          <ColocaDateField
            name="day"
            label={t("event_field_day")}
            description={t("event_field_day_hint")}
            maxDate={new Date()}
            disabled={submitting}
          />
        )}
        <ColocaTextareaField
          name="note"
          label={t(action === "provider_responded" ? "event_field_answer" : "event_field_note")}
          placeholder={t(`event_note_placeholder_${action}`, { provider })}
          rows={4}
          maxLength={INCIDENT_NOTE_MAX}
          count
          disabled={submitting}
        />
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-foreground">{t("event_field_attachments")}</p>
          <IncidentAttachmentsPicker value={files} onChange={setFiles} disabled={submitting} />
        </div>
        {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
      </ColocaForm>
    </ColocaDialog>
  );
}
